import { supabase } from "@/lib/supabase";

// Tokens are only ever mutated through these SECURITY DEFINER RPCs (see
// supabase/schema.sql). The client never writes the `tokens` column directly,
// so balances can't be forged from the browser.

export const REROLL_TOKEN_COST = 100;
export const SHARE_BONUS = 5;
export const DAILY_BONUS = 10;
// Submitting a "vs friend" (multiplayer) run to the leaderboard costs tokens —
// solo runs submit for free.
export const MULTIPLAYER_SUBMIT_COST = 50;
// Re-spinning a SINGLE stat on the results screen (vs. the 100-token full
// reroll). Cheap and granular, but capped per run so it stays a "fix one bad
// roll" mulligan rather than a way to grind a perfect build with tokens.
export const SINGLE_WHEEL_REROLL_COST = 25;
export const MAX_SINGLE_WHEEL_REROLLS = 3;

// Atomically spends `amount` tokens. Throws if the balance is insufficient
// (the check happens inside the DB transaction). Returns the new balance.
export async function spendTokens(
  amount: number,
  reason: string,
): Promise<number> {
  const { data, error } = await supabase.rpc("spend_tokens", {
    p_amount: amount,
    p_reason: reason,
  });
  if (error) throw error;
  return data as number;
}

// Awards the +5 share bonus the first time a given score is shared. Returns
// whether the bonus was granted this call plus the resulting balance.
export async function claimShareBonus(
  scoreId: string,
): Promise<{ awarded: boolean; balance: number }> {
  const { data, error } = await supabase.rpc("claim_share_bonus", {
    p_score_id: scoreId,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return { awarded: Boolean(row?.awarded), balance: row?.balance ?? 0 };
}

// ADMIN ONLY: grants (or, with a negative amount, removes) tokens on the
// caller's own account. Backed by the `admin_grant_tokens` RPC, which refuses
// to run for non-admins server-side. Returns the new balance.
export async function adminGrantTokens(amount: number): Promise<number> {
  const { data, error } = await supabase.rpc("admin_grant_tokens", {
    p_amount: amount,
  });
  if (error) throw error;
  return data as number;
}

// Grants the escalating daily login bonus if 24h have elapsed (reward = streak
// day * 5, day 1..7). Returns whether it was granted, the resulting balance,
// the reward amount, and the current streak day.
export async function claimDailyBonus(): Promise<{
  claimed: boolean;
  balance: number;
  reward: number;
  streak: number;
}> {
  const { data, error } = await supabase.rpc("claim_daily_bonus");
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return {
    claimed: Boolean(row?.claimed),
    balance: row?.balance ?? 0,
    reward: row?.reward ?? 0,
    streak: row?.streak ?? 0,
  };
}

// One-time sign-in bonus: +25 plus the carried-over guest token balance.
// Idempotent server-side. Returns whether it granted this call, the resulting
// balance, and the amount granted.
export async function claimSignupBonus(carryover: number): Promise<{
  claimed: boolean;
  balance: number;
  granted: number;
}> {
  const { data, error } = await supabase.rpc("claim_signup_bonus", {
    p_carryover: carryover,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return {
    claimed: Boolean(row?.claimed),
    balance: row?.balance ?? 0,
    granted: row?.granted ?? 0,
  };
}
